import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchUserById, fetchUserBookings, clearUserBookings } from '../../../../store/slices/adminSlice';
import BookingLifecycleModal from './BookingLifecycleModal';
import PassengersModal from './PassengersModal';
import { renderSortIcon } from '../../../../utils/sortIconHelper';
import adminService from '../../../../services/adminService';
import toast from 'react-hot-toast';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { 
    selectedUser: user, 
    userBookings, 
    bookingsPagination,
    loading, 
    bookingsLoading,
    error 
  } = useAppSelector((state) => state.admin);

  const { returnTo = 'users' } = location.state || {};
  const [isLifecycleModalOpen, setIsLifecycleModalOpen] = useState(false);
  const [selectedBookingForLifecycle, setSelectedBookingForLifecycle] = useState(null);
  const [isPassengersModalOpen, setIsPassengersModalOpen] = useState(false);
  const [selectedBookingForPassengers, setSelectedBookingForPassengers] = useState(null);
  
  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // Notes state
  const [bookingNotes, setBookingNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});

    // AOC modal state
  const [showAOCModal, setShowAOCModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);

  const handleOpenAOCModal = async () => {
    setLoadingPdf(true);
    setShowAOCModal(true);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/admin/users/${id}/aoc-signed-url`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to get signed URL');
      const data = await response.json();
      setPdfUrl(data.data.aoc_url);
    } catch (err) {
      console.error('Error loading AOC PDF:', err);
      toast.error('Failed to load AOC document');
      setShowAOCModal(false);
    } finally {
      setLoadingPdf(false);
    }
  };
 
  const handleCloseAOCModal = () => {
    setShowAOCModal(false);
    setPdfUrl('');
  };
  
  const handleOpenLifecycle = (booking) => {
    setSelectedBookingForLifecycle(booking);
    setIsLifecycleModalOpen(true);
  };

  const handleOpenPassengers = (booking) => {
    setSelectedBookingForPassengers(booking);
    setIsPassengersModalOpen(true);
  };

  const handleSaveNotes = async (bookingToken) => {
    setSavingNotes(prev => ({ ...prev, [bookingToken]: true }));
    try {
      await adminService.updateBookingNotes(bookingToken, bookingNotes[bookingToken]);
      toast.success('Notes saved successfully');
      
      // Refresh bookings list to show updated notes
      dispatch(fetchUserBookings({
        id,
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          sort_by: sortBy || 'id',
          sort_order: sortOrder
        }
      }));
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(prev => ({ ...prev, [bookingToken]: false }));
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchUserById(id));
    }
    return () => {
      dispatch(clearUserBookings());
    };
  }, [id, dispatch]);

  // Fetch bookings when pagination/search/sort changes
  useEffect(() => {
    if (id) {
      dispatch(fetchUserBookings({
        id,
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          sort_by: sortBy || 'id',
          sort_order: sortOrder
        }
      }));
    }
  }, [id, currentPage, itemsPerPage, searchTerm, sortBy, sortOrder, dispatch]);

  // Initialize notes from bookings
  useEffect(() => {
    if (userBookings) {
      const notes = {};
      userBookings.forEach(booking => {
        notes[booking.booking_token] = booking.notes || '';
      });
      setBookingNotes(notes);
    }
  }, [userBookings]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const getSortIcon = (column) => {
    return renderSortIcon(column, sortBy, sortOrder);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page on limit change
  };

  if (loading) {
    return <div className="p-12 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="p-12 text-center text-red-500">Error: {error}</div>;
  }

  if (!user) {
    return <div className="p-12 text-center">User not found</div>;
  }

  const totalPages = bookingsPagination.total_pages || 1;

  return (
    <div className="p-4 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">{user.user_type} Details</h1>
        <button
          onClick={() => navigate(`/admin/${returnTo}`)}
          className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-black bg-[#ffd200] rounded hover:opacity-90 transition-opacity"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
      </div>

      {/* User Details Container - Image and Details in one white box */}
      <div className="bg-white rounded shadow-sm mb-6 p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Profile Image */}
          <div className="w-full md:w-[230px] flex-shrink-0">
            <div className="w-full h-[155px] bg-gray-200 rounded-[8px] flex flex-col items-center justify-center text-gray-400">
              {user.image_url ? (
                <img
                  src={user.image_url}
                  alt="Profile"
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <>
                  <i className="far fa-images text-3xl text-gray-300 mb-1"></i>
                  <span className="text-xs font-medium text-gray-400">No Image Available</span>
                </>
              )}
            </div>

            {/* AOC link + expiry below image (Airliner only) */}
            {user.user_type === 'Airliner' && (
              <div className="mt-3 text-center">
                {user.aoc ? (
                  <button
                    onClick={handleOpenAOCModal}
                    className="text-[#03538f] text-sm font-semibold bg-transparent border-none cursor-pointer p-0"
                  >
                    AOC Document
                  </button>
                ) : (
                  <span className="text-gray-400 text-xs italic">No AOC Document</span>
                )}
                <p style={{ fontSize: '14px', lineHeight: '1.42857143', color: '#333' }} className="mt-1">
                  Valid Till{' '}
                  {user.not_applicable_expiry === 'Not Applicable'
                    ? 'Not Applicable'
                    : user.expiry_date
                      ? (() => {
                        const d = new Date(user.expiry_date);
                        const dd = String(d.getDate()).padStart(2, '0');
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const yyyy = d.getFullYear();
                        const hh = String(d.getHours()).padStart(2, '0');
                        const min = String(d.getMinutes()).padStart(2, '0');
                        const ss = String(d.getSeconds()).padStart(2, '0');
                        return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
                      })()
                      : 'Not Available'}
                </p>
              </div>
            )}
          </div>

          {/* User Details Grid */}
          <div className="flex-1">
            {/* Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-t border-b border-[#e5e7eb] py-3">
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">First Name</span>
                <span className="text-gray-900 text-sm font-bold">{user.first_name || '-'}</span>
              </div>
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">Last Name</span>
                <span className="text-gray-900 text-sm font-bold">{user.last_name || '-'}</span>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-[#e5e7eb] py-3">
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">Account Type</span>
                <span className="text-gray-900 text-sm font-bold">{user.account_type || '-'}</span>
              </div>
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">User Type</span>
                <span className="text-gray-900 text-sm font-bold">{user.user_type || '-'}</span>
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-[#e5e7eb] py-3">
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">Email</span>
                <span className="text-gray-900 text-sm font-bold truncate">{user.email || '-'}</span>
              </div>
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">Mobile Number</span>
                <span className="text-gray-900 text-sm font-bold">
                  {user.mobile_number ? `${user.countryCode || ''} ${user.mobile_number}` : '-'}
                </span>
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-b border-[#e5e7eb] py-3">
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">Status</span>
                <span className="text-gray-900 text-sm font-bold">{user.status || '-'}</span>
              </div>
              <div className="flex items-center justify-between px-6">
                <span className="text-gray-500 text-xs font-normal">User ID</span>
                <span className="text-gray-900 text-sm font-bold">{user.user_id || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {
        user.user_type === 'JetSetter' && (
          <>
            {/* Bookings Table with Pagination */}
            <div className="bg-white rounded shadow-sm">
              {/* Pagination Controls at Top */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Display</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-600">Records</span>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Search:</span>
                    <input
                      type="search"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
                      placeholder="Search..."
                    />
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex items-center">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-l bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(totalPages, 6))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1.5 text-sm border-t border-b border-r border-gray-300 ${
                            currentPage === pageNum
                              ? 'bg-[#ffd200] text-black'
                              : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-r bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300">#</th>
                      <th onClick={() => handleSort('booking_token')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Booking ID {getSortIcon('booking_token')}
                      </th>
                      <th onClick={() => handleSort('aircraft_name')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Aircraft Name {getSortIcon('aircraft_name')}
                      </th>
                      <th onClick={() => handleSort('registration_number')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Reg No. {getSortIcon('registration_number')}
                      </th>
                      <th onClick={() => handleSort('flight_number')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Flight Number {getSortIcon('flight_number')}
                      </th>
                      <th onClick={() => handleSort('departure_airport')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Dep. Airport {getSortIcon('departure_airport')}
                      </th>
                      <th onClick={() => handleSort('departure_date_time')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        ETD {getSortIcon('departure_date_time')}
                      </th>
                      <th onClick={() => handleSort('arrival_airport')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Arr. Airport {getSortIcon('arrival_airport')}
                      </th>
                      <th onClick={() => handleSort('arrival_date_time')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        ETA {getSortIcon('arrival_date_time')}
                      </th>
                      <th onClick={() => handleSort('selected_seat')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Selected Seat {getSortIcon('selected_seat')}
                      </th>
                      <th onClick={() => handleSort('amount')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Amount {getSortIcon('amount')}
                      </th>
                      <th onClick={() => handleSort('booking_status')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Booking Status {getSortIcon('booking_status')}
                      </th>
                      <th onClick={() => handleSort('transaction_status')} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 cursor-pointer hover:bg-gray-50">
                        Payment Status {getSortIcon('transaction_status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300">Notes</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookingsLoading ? (
                      <tr>
                        <td colSpan="15" className="px-4 py-8 text-center text-sm text-gray-500">
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Loading bookings...
                        </td>
                      </tr>
                    ) : userBookings && userBookings.length > 0 ? (
                      userBookings.map((booking, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{booking.reference_number || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{booking.aircraft_name || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 uppercase">{booking.registration_number || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 uppercase">{booking.flight_number || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{booking.dep_airport || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                            {booking.departure_date_time ? new Date(booking.departure_date_time).toLocaleString('en-GB', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            }).replace(',', '') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{booking.arr_airport || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                            {booking.arrival_date_time ? new Date(booking.arrival_date_time).toLocaleString('en-GB', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            }).replace(',', '') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{booking.selected_seat || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                            ${booking.amount ? Number(booking.amount).toFixed(2) : '0.00'}
                          </td>
                          <td className="px-4 py-3 text-sm border-r border-gray-200">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              booking.booking_status === 'Confirmed' || booking.booking_status === 'Completed'
                                ? 'bg-green-100 text-green-800' 
                                : booking.booking_status === 'Pending' 
                                ? 'bg-yellow-100 text-yellow-800'
                                : booking.booking_status === 'Cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.booking_status || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm border-r border-gray-200">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              booking.transaction_status === 'Success' || booking.transaction_status === 'Completed' 
                                ? 'bg-green-100 text-green-800' 
                                : booking.transaction_status === 'Pending' 
                                ? 'bg-yellow-100 text-yellow-800'
                                : booking.transaction_status === 'Failed' || booking.transaction_status === 'Cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.transaction_status || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-r border-gray-200">
                            <div className="flex items-center gap-2">
                              <textarea
                                value={bookingNotes[booking.booking_token] || ''}
                                onChange={(e) => setBookingNotes(prev => ({ ...prev, [booking.booking_token]: e.target.value }))}
                                className="w-48 h-8 px-2 py-1 text-sm border border-gray-300 rounded resize-y focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
                                placeholder="Add notes..."
                              />
                              <button
                                onClick={() => handleSaveNotes(booking.booking_token)}
                                disabled={savingNotes[booking.booking_token]}
                                className="w-8 h-8 rounded bg-[#03538f] text-white flex items-center justify-center hover:bg-[#2d4373] transition-colors disabled:opacity-50"
                                title="Save Note"
                              >
                                <i className={`fas ${savingNotes[booking.booking_token] ? 'fa-spinner fa-spin' : 'fa-save'} text-xs`}></i>
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => handleOpenPassengers(booking)}
                                className="w-8 h-8 rounded bg-[#3b5998] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                                title="Show Passengers Details"
                              >
                                <i className="fas fa-eye text-xs"></i>
                              </button>
                              <button 
                                onClick={() => handleOpenLifecycle(booking)}
                                className="w-8 h-8 rounded bg-white border border-gray-300 text-black flex items-center justify-center hover:bg-gray-50 transition-colors"
                                title="Booking Lifecycle"
                              >
                                <i className="fas fa-history text-xs"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="15" className="px-4 py-8 text-center text-sm text-gray-500">
                          No bookings found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer - Showing entries info */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {userBookings && userBookings.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, bookingsPagination.total)} of {bookingsPagination.total} entries
                </div>
              </div>
            </div>
          </>
        )
      }

      {/* Booking Lifecycle Modal */}
      <BookingLifecycleModal 
        isOpen={isLifecycleModalOpen} 
        onClose={() => setIsLifecycleModalOpen(false)} 
        booking={selectedBookingForLifecycle}
      />

      {/* Passengers Modal */}
      <PassengersModal 
        isOpen={isPassengersModalOpen} 
        onClose={() => setIsPassengersModalOpen(false)} 
        booking={selectedBookingForPassengers}
      />

      {/* AOC Document Modal */}
      {showAOCModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 flex flex-col" style={{ maxHeight: '90vh' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-500">AOC Document</h4>
              <button
                onClick={handleCloseAOCModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-hidden p-4 flex items-center justify-center" style={{ minHeight: '500px' }}>
              {loadingPdf ? (
                <div className="flex flex-col items-center gap-3 text-gray-500">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#ffd200]"></div>
                  <span className="text-sm">Loading document...</span>
                </div>
              ) : pdfUrl ? (
                <embed
                  src={pdfUrl}
                  type="application/pdf"
                  className="w-full"
                  style={{ height: '500px' }}
                />
              ) : (
                <p className="text-gray-500 text-sm italic">Failed to load document.</p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-3 border-t border-gray-200">
              <button
                onClick={handleCloseAOCModal}
                className="px-4 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
